import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Flame, TrendingUp, TrendingDown, BarChart3, Loader2, RefreshCw, Activity } from 'lucide-react';

const KINDS = [
  { key: 'gainers',  label: 'Gainers',   icon: TrendingUp },
  { key: 'losers',   label: 'Losers',    icon: TrendingDown },
  { key: 'volume',   label: 'Volume',    icon: BarChart3 },
  { key: 'trending', label: 'Trending',  icon: Activity },
];

/**
 * Heatmap — Phase 53.C. Tile grid colored by 24h change %. Tiles are
 * sized (font-size / weight) by volume so bigger movers/liquidity
 * pop naturally. Clicking a tile jumps into the chart for that pair.
 */
export default function Heatmap({ onPick }) {
  const [kind, setKind] = useState('gainers');
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [quote, setQuote] = useState('USDT');

  const load = async (k, q = quote) => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.get('/crypto/heatmap', { params: { kind: k, limit: 60, quote: q } });
      setItems(data?.items || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'heatmap failed');
    } finally { setBusy(false); }
  };

  useEffect(() => {
    load(kind, quote);
    // Auto-refresh every 20s so the heatmap stays "live" without
    // needing a WS layer for it (we already stream tickers, but the
    // ranking itself changes slowly).
    const iv = setInterval(() => load(kind, quote), 20_000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, quote]);

  const maxVol = useMemo(() => Math.max(1, ...items.map((i) => i.volume || 0)), [items]);

  const tileColor = (pct) => {
    if (pct == null) return { bg: 'rgba(255,255,255,0.03)', text: 'text-[var(--txt-secondary)]' };
    // Clamp to ±15% for intensity mapping.
    const clamped = Math.max(-15, Math.min(15, pct));
    const alpha = 0.08 + (Math.abs(clamped) / 15) * 0.55;
    if (pct >= 0) return { bg: `rgba(0,255,136,${alpha})`, text: 'text-buy' };
    return { bg: `rgba(255,51,102,${alpha})`, text: 'text-sell' };
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="crypto-heatmap">
      <div className="flex items-center gap-2 border-b border-[var(--border-default)] bg-[var(--bg-panel)] px-3 py-2">
        <Flame size={14} className="text-cyan-sys" />
        <div className="text-[10px] tracking-[0.35em] uppercase text-cyan-sys">// heatmap</div>
        <div className="flex items-center gap-1 ml-3">
          {KINDS.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setKind(key)}
              data-testid={`heatmap-kind-${key}`}
              className={`px-2 py-1 border text-[9px] tracking-widest uppercase flex items-center gap-1 ${kind === key ? 'border-cyan-sys text-cyan-sys' : 'border-[var(--border-default)] text-[var(--txt-secondary)] hover:text-cyan-sys'}`}
            >
              <Icon size={10} /> {label}
            </button>
          ))}
        </div>
        <button
          onClick={() => load(kind, quote)}
          disabled={busy}
          data-testid="heatmap-refresh"
          className="ml-auto px-2 py-1 border border-cyan-sys text-cyan-sys text-[9px] tracking-widest uppercase hover:bg-cyan-sys hover:text-black disabled:opacity-40 flex items-center gap-1"
        >
          {busy ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
          refresh
        </button>
        <select value={quote} onChange={(e) => setQuote(e.target.value)}
                data-testid="heatmap-quote"
                className="bg-black border border-[var(--border-default)] text-[9px] uppercase tracking-widest px-2 py-1 text-[var(--txt-secondary)]">
          {['USDT', 'USDC', 'USD', 'BTC', 'ETH'].map((q) => <option key={q} value={q}>{q}</option>)}
        </select>
      </div>

      <div className="flex-1 min-h-0 lg:overflow-auto p-3">
        {err && <div className="text-[10px] text-sell mb-2">// {err}</div>}
        {!items.length && !busy && <div className="text-[10px] text-[var(--txt-muted)]">// no data</div>}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-1.5">
          {items.map((it) => {
            const c = tileColor(it.change_pct);
            const volRatio = (it.volume || 0) / maxVol;
            const fontSize = 10 + Math.round(volRatio * 4);   // 10–14px
            return (
              <button
                key={it.symbol}
                onClick={() => onPick?.({ symbol: it.symbol, base: it.base, quote: it.quote })}
                data-testid={`heatmap-tile-${it.symbol}`}
                style={{ backgroundColor: c.bg }}
                className={`border border-[var(--border-default)] p-2 text-left hover:border-cyan-sys transition-colors ${c.text}`}
              >
                <div className="font-mono tracking-tight leading-tight" style={{ fontSize: `${fontSize}px` }}>
                  {it.base}
                </div>
                <div className={`font-mono tabular-nums text-[10px] leading-tight ${c.text}`}>
                  {(it.change_pct ?? 0) >= 0 ? '+' : ''}{(it.change_pct ?? 0).toFixed(2)}%
                </div>
                <div className="text-[9px] text-[var(--txt-muted)] tabular-nums leading-tight">
                  ${Number(it.price ?? 0).toFixed(it.price < 1 ? 5 : 2)}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="border-t border-[var(--border-default)] px-3 py-1 text-[9px] tracking-widest uppercase text-[var(--txt-muted)] flex justify-between">
        <span>{items.length} pairs · sorted by {kind}</span>
        <span>tile size = 24h volume</span>
      </div>
    </div>
  );
}
